import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { EntityDialogForm } from "components/FormDialog/EntityDialogForm";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import {
  addCategory,
  adminGetAllLocationCategories,
  deleteCategory,
  updateCategory,
} from "redux/features/Admin/categorys/categorySlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";

export interface CategoriesPageProps {
  className?: string;
}

interface Category {
  id: number;
  designation: string;
}

interface ModalState {
  isCategory: boolean;
  isDeleteModalOpen: boolean;
  selectedItem: Category | null;
  loading: boolean;
}

const CategoriesPage: FC<CategoriesPageProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    categories: categoriesList,
    totalCount,
    totalPages,
    currentPage,
    loading,
    error,
  } = useAppSelector((state) => ({
    categories: state.featureCategory?.allCategories?.categories || [],
    totalCount: state.featureCategory?.allCategories?.totalCount || 0,
    totalPages: state.featureCategory?.allCategories?.totalPages || 0,
    currentPage: state.featureCategory?.allCategories?.currentPage || 1,
    loading: state.featureCategory?.loading || "idle",
    error: state.featureCategory?.error || null,
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isCategory: false,
    isDeleteModalOpen: false,
    selectedItem: null,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllLocationCategories({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleEdit = (item: Category) => {
    setModalState({ ...modalState, selectedItem: item, isCategory: true });
  };

  const handleDeleteModalOpen = (category: Category) => {
    setModalState({ ...modalState, selectedItem: category, isDeleteModalOpen: true });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      setModalState((prev) => ({ ...prev, loading: true }));
      await dispatch(deleteCategory(modalState.selectedItem.id));
      setModalState((prev) => ({ ...modalState, isDeleteModalOpen: false, loading: false, selectedItem: null }));
      await dispatch(adminGetAllLocationCategories({ page, searchTerm: debouncedSearchTerm }));
    }
  };

  const handleConfirmUpdate = async (payload: { designation: string }) => {
    if (!modalState.selectedItem) return;
    setModalState({ ...modalState, loading: true });
    await dispatch(updateCategory({ data: payload, id: modalState.selectedItem.id })).then(() => {
      setModalState({ ...modalState, loading: false, isCategory: false, selectedItem: null });
      dispatch(adminGetAllLocationCategories({ page, searchTerm: debouncedSearchTerm }));
    });
  };

  const handleTableAction = (action: string, item: Category) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "edit") {
      handleEdit(item);
    }
  };

  const columns = [
    {
      key: "designation",
      label: "Designation",
      render: (item: Category) => <span>{item.designation}</span>,
    },
    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <HiOutlineSquare3Stack3D className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Liste Des Catégories
              </h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary
                onClick={() => setModalState({ ...modalState, isCategory: true })}
                className="btn-primary-gradient"
                sizeClass="px-4 py-2"
              >
                Ajouter catégorie
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par désignation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search categories"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {error && (
            <div className="py-4 text-center text-red-500 dark:text-red-400">
              Error: {error || "Failed to load categories"}
            </div>
          )}

          {loading !== "pending" && !error && (
            <>
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={() => {
                    setModalState({ ...modalState, isDeleteModalOpen: false, selectedItem: null });
                  }}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.designation || "",
                  }}
                />
              )}

              {modalState.isCategory && (
                <EntityDialogForm
                  open={modalState.isCategory}
                  handleClose={() =>
                    setModalState({
                      ...modalState,
                      isCategory: false,
                      selectedItem: null,
                      isDeleteModalOpen: false,
                    })
                  }
                  isLoading={modalState.loading}
                  addfn={(payload: any) => dispatch(addCategory(payload))}
                  updatefn={handleConfirmUpdate}
                  id={modalState.selectedItem?.id}
                  value={modalState.selectedItem?.designation}
                  addfnTitle="Ajouter catégorie"
                  updatefnTitle="Modifier catégorie"
                  onSubmitSuccess={() => {
                    dispatch(adminGetAllLocationCategories({ page: 1, searchTerm: "" }));
                  }}
                />
              )}

              <DynamicTable
                columns={columns}
                data={categoriesList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["edit", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default CategoriesPage;
